import { useCallback, useEffect, useRef, useState } from "react";
import { GetProtocols } from "@/services/fhir/questionnaireresponse/getProtocols";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase, PatientsProgress } from "src-next/modules/clinical-insights/loadPhase";
import { fetchPatientsParallel } from "src-next/modules/clinical-insights/helpers/fetchPatientsParallel";
import { normalizePatientId, patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

export interface ProtocolCountRow {
  protocolId: string;
  label: string;
  count: number;
}

const protocolCountsCache = new Map<string, ProtocolCountRow[]>();

function protocolCountsCacheKey(
  author: string,
  practitionerFilter: string,
  startDate?: string,
  endDate?: string
): string {
  return `${author}|${practitionerFilter}|${startDate ?? ""}|${endDate ?? ""}`;
}

interface Args {
  author: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  selectedProtocolIds: string[];
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  fetchEnabled?: boolean;
}

export function useProtocolAnalysisData({
  author,
  practitionerFilter,
  dateRange,
  selectedProtocolIds,
  onLoadPhaseChange,
  fetchEnabled = true,
}: Args) {
  const [protocols, setProtocols] = useState<ProtocolCountRow[]>([]);
  const [protocolLabels, setProtocolLabels] = useState<Map<string, string>>(
    () => new Map()
  );
  const [patientRows, setPatientRows] = useState<InsightPatientRow[]>([]);
  const [loadPhase, setLoadPhase] = useState<LoadPhase>("idle");
  const [patientsProgress, setPatientsProgress] = useState<PatientsProgress>({
    loaded: 0,
    total: 0,
  });
  const hasDataRef = useRef(false);
  const fetchGenRef = useRef(0);
  const recordsInFlightRef = useRef(false);
  const patientsInFlightRef = useRef(false);
  const fetchEnabledRef = useRef(fetchEnabled);
  fetchEnabledRef.current = fetchEnabled;
  const onLoadPhaseChangeRef = useRef(onLoadPhaseChange);
  onLoadPhaseChangeRef.current = onLoadPhaseChange;

  const syncPhase = useCallback((phase: LoadPhase) => {
    setLoadPhase(phase);
    if (fetchEnabledRef.current) onLoadPhaseChangeRef.current?.(phase);
  }, []);

  const finishRecordsPhase = useCallback(() => {
    recordsInFlightRef.current = false;
    if (!patientsInFlightRef.current) syncPhase("done");
  }, [syncPhase]);

  const fetchProtocolCounts = useCallback(async () => {
    if (!author) return;

    const cacheKey = protocolCountsCacheKey(
      author,
      practitionerFilter,
      dateRange?.startDate,
      dateRange?.endDate
    );
    const cached = protocolCountsCache.get(cacheKey);
    if (cached) {
      setProtocols(cached);
      setProtocolLabels(new Map(cached.map((p) => [p.protocolId, p.label])));
      hasDataRef.current = true;
      finishRecordsPhase();
      return;
    }

    recordsInFlightRef.current = true;
    syncPhase("records");
    try {
      const protocolsBundle = await GetProtocols({ author });
      const protocolEntries: { resource?: fhir5.QuestionnaireResponse }[] =
        protocolsBundle?.entry || [];

      const protocolsList = protocolEntries
        .map((e) => e?.resource)
        .filter((r): r is fhir5.QuestionnaireResponse => !!r && r.resourceType === "QuestionnaireResponse")
        .filter((r) => !!r.id);

      if (!protocolsList.length) {
        setProtocols([]);
        setProtocolLabels(new Map());
        hasDataRef.current = true;
        finishRecordsPhase();
        return;
      }

      const results = await Promise.allSettled(
        protocolsList.map(async (resource) => {
          const id = resource.id as string;
          const summary = await GetQuestionnaireResponse({
            author,
            practitioner: practitionerFilter,
            hasProtocols: true,
            protocol: id,
            count_num: true,
            startDate: dateRange?.startDate,
            endDate: dateRange?.endDate,
          });
          const total = Number.isFinite(summary?.total) ? summary.total : 0;
          const nameItem = resource.item?.find(
            (item) => item.linkId === "438586562091"
          );
          const protocolName =
            nameItem?.answer?.[0]?.valueString ||
            resource.title ||
            resource.name ||
            `Protocolo ${id}`;
          const label =
            typeof protocolName === "string" ? protocolName : `Protocolo ${id}`;
          return { protocolId: id, label, count: total };
        })
      );

      const ok = results
        .filter(
          (r): r is PromiseFulfilledResult<ProtocolCountRow> => r.status === "fulfilled"
        )
        .map((r) => r.value)
        .filter((p) => p.count > 0)
        .sort((a, b) => b.count - a.count);

      setProtocols(ok);
      setProtocolLabels(new Map(ok.map((p) => [p.protocolId, p.label])));
      if (ok.length > 0) protocolCountsCache.set(cacheKey, ok);
      hasDataRef.current = true;
      finishRecordsPhase();
    } catch (e) {
      console.error("Erro ao buscar protocolos:", e);
      setProtocols([]);
      setProtocolLabels(new Map());
      recordsInFlightRef.current = false;
      syncPhase("error");
    }
  }, [
    author,
    practitionerFilter,
    dateRange?.startDate,
    dateRange?.endDate,
    syncPhase,
    finishRecordsPhase,
  ]);

  useEffect(() => {
    if (author && fetchEnabled) fetchProtocolCounts();
  }, [author, fetchProtocolCounts, fetchEnabled]);

  useEffect(() => {
    const ids = selectedProtocolIds;
    if (!author || ids.length === 0) {
      setPatientRows([]);
      patientsInFlightRef.current = false;
      if (hasDataRef.current && !recordsInFlightRef.current) syncPhase("done");
      return;
    }

    const gen = ++fetchGenRef.current;
    let cancelled = false;

    (async () => {
      patientsInFlightRef.current = true;
      syncPhase("patients");
      try {
      setPatientsProgress({ loaded: 0, total: ids.length });

      const rowMap = new Map<string, InsightPatientRow>();
      const patientIds = new Set<string>();

      for (let i = 0; i < ids.length; i++) {
        if (cancelled || fetchGenRef.current !== gen) return;
        const protocolId = ids[i];
        const label =
          protocols.find((p) => p.protocolId === protocolId)?.label ??
          `Protocolo ${protocolId}`;

        try {
          const bundle = await GetQuestionnaireResponse({
            author,
            practitioner: practitionerFilter,
            hasProtocols: true,
            protocol: protocolId,
            startDate: dateRange?.startDate,
            endDate: dateRange?.endDate,
          });
          const entries = bundle?.entry || [];
          for (const entry of entries) {
            const resource = entry?.resource as fhir5.QuestionnaireResponse | undefined;
            if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;
            const patientRef = resource.subject?.reference || "";
            const patientId = normalizePatientId(patientRef);
            if (!patientId) continue;
            patientIds.add(patientId);

            const date = resource.authored
              ? new Date(resource.authored).toLocaleDateString()
              : "Não informado";
            const pract = resource.author?.display || "Não informado";

            if (!rowMap.has(patientId)) {
              rowMap.set(patientId, {
                patientId,
                patientName: "…",
                items: [],
              });
            }
            rowMap.get(patientId)!.items.push({
              itemKey: protocolId,
              label,
              statusKey: "applied",
              statusLabel: "Aplicado",
              practitioner: pract,
              date,
            });
          }
        } catch (e) {
          console.error("Erro ao buscar pacientes do protocolo:", protocolId, e);
        }

        setPatientsProgress({ loaded: i + 1, total: ids.length });
      }

      if (cancelled || fetchGenRef.current !== gen) return;

      const patientRefs = Array.from(patientIds).map((id) => `Patient/${id}`);
      const patients = await fetchPatientsParallel(patientRefs, {
        requestId: gen,
        isRequestCurrent: (id) => !cancelled && fetchGenRef.current === id,
        onProgress: (loaded, total) => {
          if (!cancelled && fetchGenRef.current === gen) {
            setPatientsProgress({ loaded, total });
          }
        },
      });

      if (cancelled || fetchGenRef.current !== gen) return;

      const rows = Array.from(rowMap.values()).map((row) => ({
        ...row,
        patientName: patientDisplayName(patients.get(row.patientId)),
        patient: patients.get(row.patientId),
      }));

      setPatientRows(rows.sort((a, b) => b.items.length - a.items.length));
      } finally {
        if (fetchGenRef.current === gen) {
          patientsInFlightRef.current = false;
          if (!cancelled && !recordsInFlightRef.current) syncPhase("done");
        }
      }
    })();

    return () => {
      cancelled = true;
      patientsInFlightRef.current = false;
    };
  }, [
    author,
    practitionerFilter,
    dateRange?.startDate,
    dateRange?.endDate,
    selectedProtocolIds,
    syncPhase,
    protocols,
  ]);

  const rankingItems: InsightChartItem[] = protocols.map((p) => ({
    key: p.protocolId,
    label: p.label,
    value: p.count,
  }));

  return {
    protocols,
    rankingItems,
    patientRows,
    loadPhase,
    patientsProgress,
    hasData: hasDataRef.current,
    retry: fetchProtocolCounts,
  };
}
